import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randChanceBoolean,
  randNumber,
  randSentence,
} from '@ngneat/falso'
import { addToDate } from '@/utils/date/calculation.js'
import { formatToDateTimeISO } from '@/utils/date/conversion.js'
import type {
  CONSULTATION_PATIENT_STATUSES_URL,
  CONSULTATION_PATIENT_STATUS_ITEM_URL,
} from '@/composables/consultationVitals.js'
import type { BodyByPath, SchemaAttributes } from '../../types.js'
import type { MockServer, MirageServer } from '../server.js'
import { createErrorResponse, pageable } from '../utils.js'

export type ConsultationPatientStatusData =
  SchemaAttributes<'ConsultationPatientStatus'>

type PatientStatusWriteDocument =
  | NonNullable<
      BodyByPath<'post', typeof CONSULTATION_PATIENT_STATUSES_URL>['data']
    >
  | NonNullable<
      BodyByPath<'patch', typeof CONSULTATION_PATIENT_STATUS_ITEM_URL>['data']
    >

/** Fixed anchor so factory + seeds produce stable ISO strings for list sorting. */
const MOCK_PATIENT_STATUS_EPOCH_ISO = '2023-01-01T12:00:00.000Z'

function consultationIdToDayOffset(consultationId: string | number): number {
  const raw = String(consultationId)
  const parsed = Number.parseInt(raw, 10)
  if (Number.isFinite(parsed) && String(parsed) === raw) {
    return Math.abs(parsed) % 5000
  }
  let hash = 0
  for (let i = 0; i < raw.length; i++) {
    hash = (Math.imul(31, hash) + raw.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % 5000
}

/** Mirage factory sequence → monotonic timestamps (deterministic ordering). */
function mockPatientStatusDateAddedFromSequence(sequence: number): string {
  const step = Math.max(0, sequence)
  return (
    formatToDateTimeISO(
      addToDate(MOCK_PATIENT_STATUS_EPOCH_ISO, { minutes: step }),
    ) ?? MOCK_PATIENT_STATUS_EPOCH_ISO
  )
}

/**
 * Seeds: one bucket per consultation; `statusIndex` lower → newer (matches desc sort).
 */
function mockPatientStatusDateAddedForConsultation(
  consultationId: string | number,
  statusIndex: number,
): string {
  const dayOffset = consultationIdToDayOffset(consultationId)
  return (
    formatToDateTimeISO(
      addToDate(MOCK_PATIENT_STATUS_EPOCH_ISO, {
        days: dayOffset,
        minutes: 255 - statusIndex,
      }),
    ) ?? MOCK_PATIENT_STATUS_EPOCH_ISO
  )
}

/** Sample measurement rows for Mirage (wire format; matches vitals UI). */
function mockPatientStatusMeasurements(): NonNullable<
  ConsultationPatientStatusData['measurements']
> {
  return [
    {
      key: 'weight',
      value: String(randNumber({ min: 22, max: 32, fraction: 1 })),
    },
    {
      key: 'temperature',
      value: String(randNumber({ min: 37, max: 40, fraction: 1 })),
    },
    {
      key: 'heart_rate',
      value: String(randNumber({ min: 72, max: 160 })),
    },
    {
      key: 'spo2',
      value: String(randNumber({ min: 94, max: 100 })),
    },
    {
      key: 'blood_pressure_max',
      value: String(randNumber({ min: 110, max: 140 })),
    },
    {
      key: 'blood_pressure_min',
      value: String(randNumber({ min: 65, max: 95 })),
    },
    {
      key: 'body_position',
      value: rand(['1', '2', '3']),
    },
    {
      key: 'text',
      value: randChanceBoolean({ chanceTrue: 0.55 }) ? randSentence() : null,
    },
  ]
}

const MOCK_SCHEMA_MEASUREMENT_FIELDS = [
  {
    key: 'weight',
    type: 'NUMBER' as const,
    label: 'Weight',
    unit: 'lbs',
    default: null,
    options: null,
  },
  {
    key: 'temperature',
    type: 'NUMBER' as const,
    label: 'Temperature',
    unit: '°C',
    default: null,
    options: null,
  },
  {
    key: 'heart_rate',
    type: 'NUMBER' as const,
    label: 'Heart rate',
    unit: 'bpm',
    default: null,
    options: null,
  },
  {
    key: 'spo2',
    type: 'NUMBER' as const,
    label: 'SpO₂',
    unit: '%',
    default: null,
    options: null,
  },
  {
    key: 'blood_pressure_max',
    type: 'NUMBER' as const,
    label: 'Systolic (max.) blood pressure',
    unit: 'mmHg',
    default: null,
    options: null,
  },
  {
    key: 'blood_pressure_min',
    type: 'NUMBER' as const,
    label: 'Diastolic (min.) blood pressure',
    unit: 'mmHg',
    default: null,
    options: null,
  },
  {
    key: 'body_position',
    type: 'SELECT' as const,
    label: 'Body position during measurement',
    unit: null,
    default: '1',
    options: [
      { value: '1', label: 'Laterally recumbent' },
      { value: '2', label: 'Sternal' },
      { value: '3', label: 'Standing' },
    ],
  },
  {
    key: 'text',
    type: 'TEXT' as const,
    label: 'Notes',
    unit: null,
    default: null,
    options: null,
  },
]

export const model = (
  Model as ModelDefinition<ConsultationPatientStatusData>
).extend({
  consultation: belongsTo('consultation'),
  patient: belongsTo('patient'),
})

export const factory = Factory.extend<ConsultationPatientStatusData>({
  date_added(i = 0) {
    return mockPatientStatusDateAddedFromSequence(i)
  },
  measurements() {
    return mockPatientStatusMeasurements()
  },
})

type ConsultationPatientStatusMirageModel = {
  id?: string
  attrs: ConsultationPatientStatusData
  patientId?: string
}

function serializeStatus(model: ConsultationPatientStatusMirageModel) {
  const patientId = model.patientId
  return {
    type: 'ConsultationPatientStatus' as const,
    id: model.id,
    attributes: {
      date_added: model.attrs.date_added,
      created: model.attrs.date_added,
      modified: model.attrs.date_added,
      measurements: model.attrs.measurements,
    },
    relationships: {
      patient: patientId
        ? { data: { type: 'Patient' as const, id: String(patientId) } }
        : { data: null },
      created_user: { data: { type: 'User' as const, id: '1' } },
      modified_user: { data: { type: 'User' as const, id: '1' } },
    },
  }
}

function parseJsonApiBody(request: { requestBody?: string }): {
  data?: PatientStatusWriteDocument
} {
  if (!request.requestBody) {
    return {}
  }
  try {
    return JSON.parse(request.requestBody) as {
      data?: PatientStatusWriteDocument
    }
  } catch {
    return {}
  }
}

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/patient_statuses/schema',
    function (_schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      return {
        data: {
          type: 'ConsultationPatientStatusSchema' as const,
          id: String(consultationId),
          attributes: {
            measurements: MOCK_SCHEMA_MEASUREMENT_FIELDS,
          },
        },
      }
    },
  )

  server.get(
    '/consultations/:parent_lookup_consultation/patient_statuses',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const statuses = schema.where('consultationPatientStatus', {
        consultationId,
      })
      const sorted = statuses.sort((a, b) =>
        (b.attrs.date_added ?? '').localeCompare(a.attrs.date_added ?? ''),
      )
      const { data, meta } = pageable(sorted, request)
      return {
        data: data.models.map((m) =>
          serializeStatus(m as ConsultationPatientStatusMirageModel),
        ),
        meta: { pagination: meta },
      }
    },
  )

  server.post(
    '/consultations/:parent_lookup_consultation/patient_statuses',
    function (schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const consultation = schema.find('consultation', consultationId)
      if (!consultation) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Consultation not found' }],
        })
      }

      const { data } = parseJsonApiBody(request)
      const attrs = data?.attributes
      const patientRel = data?.relationships?.patient?.data

      if (!attrs?.date_added || !Array.isArray(attrs.measurements)) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'date_added and measurements are required' }],
        })
      }

      const patientId =
        patientRel && 'id' in patientRel ? patientRel.id : undefined
      const patient = patientId ? schema.find('patient', patientId) : null
      if (!patient) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Valid patient relationship is required' }],
        })
      }

      const record = schema.create('consultationPatientStatus', {
        consultation,
        patient,
        date_added: attrs.date_added,
        measurements: attrs.measurements,
      })

      return { data: serializeStatus(record) }
    },
  )

  server.patch(
    '/consultation_patient_statuses/:id',
    function (schema, request) {
      const id = request.params.id
      const record = schema.find('consultationPatientStatus', id)
      if (!record) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'ConsultationPatientStatus not found' }],
        })
      }

      const { data } = parseJsonApiBody(request)
      const attrs = data?.attributes
      if (!attrs || Object.keys(attrs).length === 0) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'No attributes to update' }],
        })
      }

      record.update({
        ...(typeof attrs.date_added === 'string'
          ? { date_added: attrs.date_added }
          : {}),
        ...(Array.isArray(attrs.measurements)
          ? { measurements: attrs.measurements }
          : {}),
      } as ConsultationPatientStatusData)
      return {
        data: serializeStatus(record as ConsultationPatientStatusMirageModel),
      }
    },
  )
}

export function seeds(server: MockServer) {
  const consultations = server.schema.all('consultation').models

  for (const consultation of consultations) {
    const patient = consultation.patients?.models?.[0]
    if (!patient) {
      continue
    }

    server.create('consultationPatientStatus', {
      consultation,
      patient,
      date_added: mockPatientStatusDateAddedForConsultation(
        consultation.id ?? '',
        0,
      ),
      measurements: mockPatientStatusMeasurements(),
    })
    server.create('consultationPatientStatus', {
      consultation,
      patient,
      date_added: mockPatientStatusDateAddedForConsultation(
        consultation.id ?? '',
        1,
      ),
      measurements: mockPatientStatusMeasurements(),
    })
  }
}
